import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, CircularProgress } from "@mui/material";
import { RendererProps } from "../FilePreviewModal";
import { formatBytes } from "../previewTypes";
import SheetView from "../sheet/SheetView";
import { RawSheet } from "../sheet/sheetTypes";
import { SheetLoader } from "../sheet/useSheetView";

/**
 * CSV is parsed here and nothing else happens here: filtering, sorting, hiding
 * and exporting are SheetView's job, shared with the xlsx renderer.
 */

/**
 * Guards, so that a file nobody expected cannot take the tab down with it. Both
 * are announced in the viewer when they bite - a silently shortened payroll
 * export is worse than no preview at all.
 */
const MAX_SHEET_BYTES = 25 * 1024 * 1024;
const MAX_ROWS = 200_000;

interface ParsedCsv {
  sheet: RawSheet;
  truncatedRows: number | null;
  note: string | null;
}

function toRawSheet(rows: string[][]): RawSheet {
  let columnCount = 0;
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].length > columnCount) columnCount = rows[i].length;
  }
  const matrix: string[][] = new Array(rows.length);
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (row.length === columnCount) {
      matrix[i] = row;
      continue;
    }
    const padded = row.slice();
    while (padded.length < columnCount) padded.push("");
    matrix[i] = padded;
  }
  return { id: "", name: "Sheet", matrix, columnCount };
}

const CsvRenderer: React.FC<RendererProps> = ({
  blob,
  fileName,
  fileSize,
  sourceKey,
  stackIndex,
  headerSlot,
  onDownloadOriginal,
  claimHeaderDownload,
  getPopupContainer,
}) => {
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!blob) return;
    const fileBlob = blob;
    let cancelled = false;

    const run = async () => {
      try {
        const overSize = fileBlob.size > MAX_SHEET_BYTES;
        const slice = overSize ? fileBlob.slice(0, MAX_SHEET_BYTES) : fileBlob;
        let text = await slice.text();
        if (cancelled) return;
        if (overSize) {
          // Drop the partial last line rather than showing a half-read row.
          const lastBreak = text.lastIndexOf("\n");
          if (lastBreak > 0) text = text.slice(0, lastBreak);
        }

        const Papa = (await import("papaparse")).default;
        if (cancelled) return;
        const result = Papa.parse<string[]>(text, { skipEmptyLines: false });
        const rows = (result.data as string[][]).filter(Array.isArray);

        // A file ending in a newline yields one trailing empty row; it is an
        // artefact of the format, not a row somebody typed.
        if (
          rows.length > 1 &&
          rows[rows.length - 1].length === 1 &&
          rows[rows.length - 1][0] === ""
        ) {
          rows.pop();
        }

        const overRows = rows.length > MAX_ROWS;
        const kept = overRows ? rows.slice(0, MAX_ROWS) : rows;

        const note = overSize
          ? `Showing the first ${formatBytes(MAX_SHEET_BYTES)} of this ${formatBytes(fileBlob.size)} file (${kept.length.toLocaleString()} rows). Download the file to see everything.`
          : overRows
            ? `Showing the first ${MAX_ROWS.toLocaleString()} of ${rows.length.toLocaleString()} rows. Download the file to see everything.`
            : null;

        if (cancelled) return;
        setParsed({
          sheet: toRawSheet(kept),
          truncatedRows: overSize || overRows ? kept.length : null,
          note,
        });
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to parse CSV");
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [blob]);

  const loaders = useMemo<SheetLoader[]>(() => {
    if (!parsed) return [];
    return [{ id: "", name: "Sheet", load: () => parsed.sheet }];
  }, [parsed]);

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  if (!parsed) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (parsed.sheet.matrix.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">CSV is empty.</Alert>
      </Box>
    );
  }

  return (
    <SheetView
      loaders={loaders}
      fileName={fileName}
      fileSizeLabel={formatBytes(fileSize ?? blob?.size)}
      sourceKey={sourceKey ?? fileName}
      stackIndex={stackIndex ?? 0}
      headerSlot={headerSlot}
      onDownloadOriginal={onDownloadOriginal}
      claimHeaderDownload={claimHeaderDownload}
      getPopupContainer={getPopupContainer}
      truncatedRows={parsed.truncatedRows}
      truncationNote={parsed.note}
    />
  );
};

export default CsvRenderer;
